import type { Post } from "./types";
import heroDirekt from "@/assets/news/lana-pengar-direkt.jpg";

export const POSTS_17: Record<string, Post> = {
  "lana-pengar-direkt": {
    title: "Låna pengar direkt – så får du pengarna samma dag 2026",
    intro:
      "Behöver du låna pengar direkt och känner att klockan tickar? Vi förstår – en trasig bil, en oväntad räkning eller en tandläkarnota bryr sig inte om när lönen kommer. Här går vi igenom vilka långivare som faktiskt betalar ut direkt 2026, hur snabbt pengarna kan vara på kontot, vad det kostar i kronor och vilka alternativ som ofta är både billigare och tryggare.",
    author: "Erik Lindqvist",
    authorTitle: "finansiell skribent, 9 år inom konsumentkrediter",
    published: "4 september 2026",
    updated: "4 september 2026",
    heroSrc: heroDirekt,
    heroAlt: "Person använder mobilen med BankID för att låna pengar direkt och få utbetalning samma dag",
    topLenderId: "credifi",
    bottomLenderId: "lumify",
    topLenderTitle: "Populärast när du behöver låna pengar direkt",
    bottomLenderTitle: "Vår rekommendation för snabb utbetalning utan UC",
    reviewsLenderId: "lumify",
    cta: { to: "/snabblan", label: "Jämför långivare med direktutbetalning" },
    calculator: {
      title: "Räkna på att låna pengar direkt",
      intro:
        "Innan du ansöker: se vad ett snabbt lån faktiskt kostar. Dra i reglagen så får du månadskostnad, räntekostnad, total lånekostnad och effektiv ränta.",
      note:
        "Månadskostnaden avgörs av lånebelopp, löptid och nominell ränta, men det är den effektiva räntan som visar din verkliga kostnad eftersom uppläggningsavgift och aviavgift räknas in. En längre löptid ger lägre månadskostnad men klart högre total lånekostnad – testa därför alltid flera löptider innan du signerar.",
      amount: 10000,
      minAmount: 1000,
      maxAmount: 50000,
      months: 12,
      maxMonths: 60,
      rate: 22.9,
      minRate: 5,
      maxRate: 39,
      setupFee: 350,
      invoiceFee: 29,
    },
    faq: [
      {
        q: "Hur snabbt kan man låna pengar direkt?",
        a: "Hos flera nischlångivare tar hela kedjan 5–30 minuter från signering till pengar på kontot, förutsatt att du använder BankID och har konto i en bank som stödjer realtidsbetalning. Sker utbetalningen via vanlig bankgirering kan den i stället dröja till nästa bankdag.",
      },
      {
        q: "Går det att låna pengar direkt på helgen?",
        a: "Ja, hos långivare som betalar ut via realtidsbetalning eller Swish. Bankernas ordinarie clearing ligger nere på helger, så utan realtidsutbetalning landar pengarna först på måndagen.",
      },
      {
        q: "Vad krävs för att bli beviljad?",
        a: "Du ska vara minst 18–20 år beroende på långivare, vara folkbokförd i Sverige, ha svenskt BankID och en regelbunden inkomst. Långivaren är skyldig att göra en kreditprövning innan pengarna betalas ut.",
      },
      {
        q: "Kan jag låna pengar direkt utan UC?",
        a: "Flera långivare använder Creditsafe eller Dun & Bradstreet i stället för UC. Kreditupplysningen görs alltså ändå, men den syns inte i din UC-historik och påverkar inte den bilden.",
      },
      {
        q: "Vad kostar det att låna 10 000 kr direkt?",
        a: "Med 22,9 procent nominell ränta, 350 kr i uppläggningsavgift och 29 kr per avi blir månadskostnaden cirka 970 kr på tolv månader. Totalt betalar du då runt 11 600 kr, vilket motsvarar en effektiv ränta kring 32 procent.",
      },
      {
        q: "Kan jag ångra lånet om jag ändrar mig?",
        a: "Ja. Du har 14 dagars ångerrätt enligt konsumentkreditlagen. Du betalar tillbaka lånebeloppet plus ränta för de dagar du haft pengarna, men slipper resten av kostnaden.",
      },
      {
        q: "Vad händer om jag inte kan betala i tid?",
        a: "Kontakta långivaren direkt och be om anstånd eller en ny betalningsplan innan förfallodagen. En obetald avi leder till påminnelseavgift, inkasso och i värsta fall en betalningsanmärkning som ligger kvar i tre år.",
      },
    ],
    blocks: [
      { t: "h2", text: "Låna pengar direkt – kort svar" },
      { t: "p", text: "Att låna pengar direkt är fullt möjligt 2026. Flera långivare erbjuder besked inom minuter och utbetalning samma dag, ibland på under en halvtimme. Snabbheten kostar dock: ju kortare väg till pengarna, desto högre är i regel räntan." },
      { t: "p", text: "Vi tänker inte moralisera. De flesta som söker efter direktutbetalning har en konkret utgift som inte kan vänta. Men vi tänker vara ärliga med siffrorna, för det är där skillnaden mellan ett bra och ett dåligt beslut ligger." },
      {
        t: "stats",
        items: [
          { label: "Snabbaste utbetalning", value: "5–15 min" },
          { label: "Typisk ränta snabblån", value: "19–29 %" },
          { label: "Ångerrätt enligt lag", value: "14 dagar" },
        ],
      },

      { t: "h2", text: "Så fungerar en direktutbetalning steg för steg" },
      { t: "p", text: "Hela kedjan är automatiserad. Det är därför den kan gå på minuter i stället för dagar." },
      {
        t: "steps",
        title: "Från ansökan till pengar på kontot",
        items: [
          "Du fyller i belopp och löptid och signerar ansökan med BankID.",
          "Långivaren hämtar en kreditupplysning och läser av inkomstuppgifter automatiskt.",
          "Ett kreditbeslut fattas – oftast inom någon minut.",
          "Du signerar låneavtalet, även det med BankID.",
          "Pengarna skickas via realtidsbetalning, Swish eller vanlig bankgirering.",
        ],
      },
      { t: "p", text: "Flaskhalsen är nästan aldrig långivaren, utan det sista steget. Realtidsbetalning fungerar dygnet runt, medan bankgirering följer bankernas clearingtider." },

      { t: "h2", text: "Hur snabbt kommer pengarna – realistiska tider" },
      {
        t: "table",
        caption: "Utbetalningstider beroende på metod",
        head: ["Metod", "Vardag dagtid", "Kväll och natt", "Helg"],
        rows: [
          ["Realtidsbetalning", "5–15 min", "5–15 min", "5–15 min"],
          ["Swish", "Minuter", "Minuter", "Minuter"],
          ["Bankgirering samma bank", "Samma dag", "Nästa bankdag", "Nästa bankdag"],
          ["Bankgirering annan bank", "1 bankdag", "Nästa bankdag", "Måndag"],
        ],
      },
      { t: "p", text: "Har du konto i samma bank som långivaren använder går det nästan alltid snabbare. Läs mer i vår guide om [snabblån med utbetalning direkt](/nyhet/snabblan-utbetalning-direkt)." },

      { t: "h2", text: "Vilka långivare betalar ut direkt 2026?" },
      { t: "p", text: "Marknaden domineras av nischaktörer som byggt hela sin process kring automatiska beslut. Storbankerna är billigare men handlägger sällan lika snabbt." },
      { t: "h3", text: "Nischlångivare med automatiskt beslut" },
      { t: "p", text: "De flesta ger besked inom minuter och betalar ut direkt när avtalet är signerat. Räntan ligger normalt mellan 19 och 29 procent." },
      { t: "h3", text: "Låneförmedlare" },
      { t: "p", text: "En förmedlare tar en kreditupplysning och skickar din ansökan till många banker samtidigt. Du får ofta bättre ränta, men buden kan ta några timmar." },
      { t: "h3", text: "Storbanker" },
      { t: "p", text: "Lägst ränta, hårdast krav och längst handläggning. Sällan ett alternativ när pengarna behövs samma dag." },

      { t: "h2", text: "Vad det faktiskt kostar att låna pengar direkt" },
      { t: "p", text: "Nedan visar vi vad 10 000 kr kostar med 22,9 procent nominell ränta, 350 kr i uppläggningsavgift och 29 kr per avi." },
      {
        t: "table",
        caption: "Räkneexempel: 10 000 kr, annuitetslån, 22,9 % nominell ränta",
        head: ["Löptid", "Månadskostnad", "Räntekostnad", "Total kostnad", "Effektiv ränta"],
        rows: [
          ["6 mån", "1 809 kr", "578 kr", "10 928 kr", "42,1 %"],
          ["12 mån", "969 kr", "1 274 kr", "11 624 kr", "32,3 %"],
          ["24 mån", "552 kr", "2 553 kr", "13 199 kr", "27,4 %"],
          ["36 mån", "418 kr", "3 897 kr", "14 291 kr", "25,7 %"],
        ],
      },
      { t: "p", text: "Notera paradoxen: kort löptid ger högst effektiv ränta men lägst total kostnad. Effektiv ränta är ett årsmått, medan den totala kostnaden är det du faktiskt betalar. Räkna på dina egna siffror i vår [lånekalkyl](/guider/lanekalkyl)." },

      { t: "h2", text: "Effektiv ränta – det enda talet som går att jämföra" },
      { t: "p", text: "Nominell ränta säger lite eftersom avgifterna ligger utanför. Effektiv ränta väger in uppläggningsavgift, aviavgift och löptid och är därför det enda måttet som gör två erbjudanden jämförbara." },
      {
        t: "box",
        tone: "tip",
        title: "Snabb tumregel",
        items: [
          "Jämför alltid effektiv ränta, aldrig månadskostnad.",
          "Låg månadskostnad betyder ofta lång löptid och högre totalkostnad.",
          "Fråga efter totalbeloppet i kronor – det ska stå i avtalet.",
          "Uppläggningsavgiften slår hårdast på små och korta lån.",
        ],
      },

      { t: "h2", text: "Krav för att bli beviljad" },
      {
        t: "ul",
        items: [
          "Minst 18–20 år, beroende på långivare.",
          "Folkbokförd i Sverige och svenskt BankID.",
          "Regelbunden och verifierbar inkomst.",
          "Inga aktiva skulder hos Kronofogden hos de flesta aktörer.",
          "Eget bankkonto i ditt namn – utbetalning sker aldrig till annan person.",
        ],
      },
      { t: "p", text: "Har du betalningsanmärkning finns ändå alternativ, men villkoren blir tuffare. Läs mer i vår artikel om [lån utan UC med många förfrågningar](/nyhet/lan-utan-uc-manga-forfragningar)." },

      { t: "h2", text: "Låna pengar direkt utan UC" },
      { t: "p", text: "En del långivare använder Creditsafe eller Dun & Bradstreet i stället för UC. Kreditprövningen görs fortfarande – den är obligatorisk enligt konsumentkreditlagen – men förfrågan syns inte i din UC-historik." },
      { t: "p", text: "Det är värdefullt om du redan har flera förfrågningar registrerade, eftersom många förfrågningar på kort tid i sig sänker din kreditvärdighet. Se hela listan på vår sida om [lån utan UC](/lan-utan-uc)." },

      { t: "h2", text: "Så påverkas din kreditvärdighet" },
      { t: "p", text: "Varje kreditupplysning lämnar ett spår. En eller två under ett år är oproblematiskt, men sex ansökningar samma vecka signalerar ekonomisk stress och gör att systemen sorterar bort dig." },
      {
        t: "table",
        caption: "Vad som påverkar bedömningen mest",
        head: ["Faktor", "Påverkan", "Går att förbättra"],
        rows: [
          ["Betalningsanmärkning", "Mycket stor", "Nej – tiden avgör"],
          ["Skuld hos Kronofogden", "Mycket stor", "Ja – betala av"],
          ["Antal förfrågningar", "Medel", "Ja – sprid ut ansökningarna"],
          ["Inkomstens storlek", "Stor", "Ja – på sikt"],
          ["Anställningsform", "Medel", "Ja – fast anställning väger tyngre"],
          ["Befintliga krediter", "Stor", "Ja – lös eller samla"],
        ],
      },

      { t: "h2", text: "Vanliga situationer – och vad vi skulle göra" },
      { t: "h3", text: "Bilen står still och du behöver den till jobbet" },
      { t: "p", text: "Här är ett kort lån ofta försvarbart, eftersom alternativet kostar mer i uteblivna arbetsdagar. Låna exakt reparationsbeloppet och välj kortast möjliga löptid." },
      { t: "h3", text: "Räkningen förfaller i morgon" },
      { t: "p", text: "Ring först fordringsägaren. Anstånd är gratis eller kostar en påminnelseavgift, medan ett snabblån kostar hundralappar i ränta redan första månaden." },
      { t: "h3", text: "Du ska täcka ett annat lån" },
      { t: "p", text: "Det här är den farligaste anledningen. Att låna för att betala ett lån förlänger problemet och ökar skulden. Titta i stället på ett [samlingslån](/samla-lan) eller kontakta budget- och skuldrådgivningen." },

      { t: "h2", text: "Billigare alternativ innan du lånar" },
      {
        t: "ul",
        items: [
          "Anstånd hos fordringsägaren – ofta gratis och alltid värt ett samtal.",
          "Avbetalningsplan direkt hos företaget, ibland helt räntefri.",
          "Lönegaranti eller förskott från arbetsgivaren.",
          "Kontokredit du redan har – räntan är ofta lägre än på snabblån.",
          "Kommunens budget- och skuldrådgivning – kostnadsfri och opartisk.",
        ],
      },

      { t: "h2", text: "Ditt skydd i lagen" },
      { t: "p", text: "Konsumentkreditlagen ger ett tydligt skydd. Den nominella räntan får inte överstiga Riksbankens referensränta plus 20 procentenheter, och de samlade kostnaderna för ränta, avgifter och dröjsmålsränta får aldrig överstiga lånebeloppet." },
      {
        t: "box",
        tone: "info",
        title: "Det här gäller alltid",
        items: [
          "14 dagars ångerrätt utan att du behöver ange någon anledning.",
          "Rätt att lösa lånet i förtid utan extra avgift.",
          "Obligatorisk kreditprövning – ingen seriös aktör hoppar över den.",
          "Långivaren måste ha tillstånd från Finansinspektionen.",
        ],
      },

      { t: "h2", text: "Så känner du igen en oseriös aktör" },
      { t: "p", text: "När det är bråttom blir man mindre kritisk, och det utnyttjas. Kontrollera alltid företaget i Finansinspektionens register innan du lämnar några uppgifter." },
      {
        t: "box",
        tone: "warning",
        title: "Varningssignaler",
        items: [
          "Avgift som ska betalas i förskott för att lånet ska beviljas.",
          "Löfte om lån helt utan kreditprövning.",
          "Kontakt enbart via sms, WhatsApp eller Messenger.",
          "Ingen adress, inget organisationsnummer och inga fullständiga villkor.",
        ],
      },

      { t: "h2", text: "För- och nackdelar med att låna pengar direkt" },
      {
        t: "proscons",
        pros: [
          "Pengar på kontot inom minuter",
          "Digital process utan papper eller besök",
          "Fungerar även kvällar och helger",
          "Ångerrätt och rätt att lösa lånet i förtid",
        ],
        cons: [
          "Hög effektiv ränta jämfört med banklån",
          "Uppläggningsavgift slår hårt på små belopp",
          "Lätt att låna mer än man behöver",
          "Risk för skuldspiral vid upprepade lån",
        ],
      },

      { t: "h2", text: "Sju frågor att ställa innan du signerar" },
      {
        t: "steps",
        title: "Checklista",
        items: [
          "Vad blir totalbeloppet i kronor, inte bara per månad?",
          "Vilken är den effektiva räntan?",
          "Finns uppläggningsavgift och hur stor är aviavgiften?",
          "Hur snabbt sker utbetalningen till just min bank?",
          "Vad kostar en påminnelse om jag missar en avi?",
          "Kan jag lösa lånet i förtid utan extra kostnad?",
          "Har långivaren tillstånd från Finansinspektionen?",
        ],
      },

      { t: "h2", text: "Så mycket bör du låna" },
      { t: "p", text: "Regeln är enkel: låna exakt det du behöver, inte det du blir erbjuden. Långivare visar ofta ett högre förhandsgodkänt belopp eftersom det ökar deras intäkt, men varje extra tusenlapp kostar dig ränta i hela löptiden." },
      { t: "p", text: "En rimlig gräns är att den samlade månadskostnaden för alla krediter inte överstiger tio procent av din nettolön. Ligger du över är ett nytt lån sällan lösningen." },

      { t: "h2", text: "Vad som händer om du missar en betalning" },
      { t: "p", text: "Först kommer en påminnelse med avgift, därefter ett inkassokrav och slutligen ett ärende hos Kronofogden. Det sista steget ger en betalningsanmärkning som ligger kvar i tre år och påverkar allt från mobilabonnemang till hyreskontrakt." },
      { t: "p", text: "Det goda är att kedjan går att bryta hela vägen fram. Ring långivaren så fort du vet att det blir tight – de flesta erbjuder anstånd eller en ny plan, och det kostar betydligt mindre än ett inkassoärende." },

      { t: "h2", text: "Efter utbetalningen – tre saker som gör skillnad" },
      {
        t: "ul",
        items: [
          "Lägg upp autogiro direkt så att ingen avi glöms bort.",
          "Sätt en påminnelse några dagar före förfallodagen.",
          "Betala extra när du kan – räntan räknas på skulden, så varje extra hundralapp sänker totalkostnaden.",
        ],
      },

      { t: "h2", text: "Så granskar vi långivarna" },
      { t: "p", text: "Vi väger ränta, avgifter, utbetalningstid, hur tydliga villkoren är, tillstånd hos Finansinspektionen och kundomdömen. Ingen långivare kan köpa sig till en placering. Läs mer om [hur vi granskar](/om-oss/hur-vi-granskar) och om vår [redaktion](/om-oss/redaktion)." },

      { t: "h2", text: "Sammanfattning" },
      { t: "p", text: "Att låna pengar direkt går snabbt och fungerar dygnet runt hos flera långivare 2026. Priset är en hög effektiv ränta, så låna minsta möjliga belopp, välj kortast löptid du klarar och jämför alltid effektiv ränta i stället för månadskostnad. Är utgiften inte akut är väntan nästan alltid billigare." },

      { t: "h2", text: "Olika lånetyper när du behöver pengar snabbt" },
      { t: "p", text: "Alla snabba krediter fungerar inte likadant, och skillnaden i kostnad är större än de flesta tror. Här är de vanligaste formerna och när respektive variant är rimlig." },
      { t: "h3", text: "Snabblån och mikrolån" },
      { t: "p", text: "Belopp från 1 000 kr upp till cirka 50 000 kr med automatiskt beslut och utbetalning inom minuter. Räntan är hög, men löptiden kort. Passar när summan är liten och du vet att den kan betalas tillbaka snabbt." },
      { t: "h3", text: "Kontokredit" },
      { t: "p", text: "Ett förhandsgodkänt utrymme som kostar bara när du använder det. Har du redan en kontokredit är den nästan alltid billigare än att teckna ett nytt snabblån." },
      { t: "h3", text: "Privatlån via förmedlare" },
      { t: "p", text: "En kreditupplysning skickas till många banker samtidigt och du får flera bud. Det tar några timmar längre, men räntan blir ofta hälften så hög." },
      { t: "h3", text: "Kreditkort med räntefri period" },
      { t: "p", text: "Har du ett kort med 30–55 räntefria dagar och kan betala hela fakturan i tid är det den billigaste snabba lösningen av alla – noll kronor i kostnad." },
      {
        t: "table",
        caption: "Vad de olika alternativen kostar för 10 000 kr under tre månader",
        head: ["Typ", "Typisk ränta", "Ungefärlig kostnad", "Tid till pengar"],
        rows: [
          ["Kreditkort inom räntefri period", "0 %", "0 kr", "Direkt"],
          ["Befintlig kontokredit", "15–25 %", "400–600 kr", "Minuter"],
          ["Privatlån via förmedlare", "7–15 %", "500–800 kr", "Timmar–1 dag"],
          ["Snabblån utan UC", "19–29 %", "800–1 100 kr", "Minuter"],
        ],
      },
      { t: "p", text: "Skillnaden mellan det billigaste och det dyraste alternativet är alltså över tusen kronor – för exakt samma pengar under exakt lika lång tid." },

      { t: "h2", text: "Tidslinjen när du behöver pengar i dag" },
      { t: "p", text: "Många tror att valet står mellan att låna dyrt nu eller vänta i veckor. I praktiken finns det en mellanväg som ofta hinns med samma dag." },
      { t: "h3", text: "Första timmen" },
      { t: "p", text: "Ring fordringsägaren och fråga om anstånd. Det tar tio minuter och är gratis. Kontrollera samtidigt om du har outnyttjad kontokredit eller räntefritt kortutrymme." },
      { t: "h3", text: "Inom några timmar" },
      { t: "p", text: "Skicka en ansökan via en låneförmedlare. En enda kreditupplysning ger bud från flera banker, och besked kommer ofta samma eftermiddag." },
      { t: "h3", text: "Sista utvägen" },
      { t: "p", text: "Först om inget av ovanstående fungerar är ett snabblån med direktutbetalning rätt val. Då gäller minsta möjliga belopp och kortast möjliga löptid." },

      { t: "h2", text: "Vad vi ser hos läsare som klarar det bra" },
      { t: "p", text: "Mönstret är påfallande likt. De som klarar ett snabbt lån utan att hamna snett gör tre saker: de lånar en tydligt avgränsad summa till en konkret utgift, de sätter en återbetalningsplan redan innan pengarna kommit in, och de tar inte ett nytt lån innan det första är löst." },
      { t: "p", text: "De som får problem har oftast lånat lite för mycket, valt lång löptid för att månadskostnaden skulle se snäll ut, och sedan lagt till ytterligare en kredit när det blev tight. Det är sällan ett moraliskt misslyckande – det är en fälla som är designad att kännas rimlig i stunden." },

      { t: "h2", text: "Källor" },
      {
        t: "ul",
        items: [
          "[Konsumentverket](https://www.konsumentverket.se) – räntetak, kostnadstak och ångerrätt",
          "[Finansinspektionen](https://www.fi.se) – tillståndsregister och tillsyn",
          "[Riksbanken](https://www.riksbank.se) – referensränta",
          "[Hallå konsument](https://www.hallakonsument.se) – oberoende vägledning",
        ],
      },
    ],
  },
};
